import React from "react";
import { BasicMuiTableStyled, InputControl } from "../../../shared";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { useCostAndPrice } from "../hooks/useCostAndPrice";
import { formatWeightWithUnits } from "../../../utils";
import { PosScheduleSyncModal } from "./PosScheduleSyncModal";

export const CostAndPricingView = ({
  recipeId,
  ingredientQuery,
  basicDetailsQuery,
  isEditable,
  globalData,
}) => {
  const {
    state,
    onChange,
    validator,
    onEditClick,
    onUpdateRetailPriceClick,
    onRetailPriceChange,
    onUpdateOverHead,
    costvalidator,
    updateOverHead,
    handleSyncModal,
    onUpdateOverHeadSubmit,
  } = useCostAndPrice({
    ingredientQuery,
    recipeId,
  });

  const { isEditoverHead, isEditPrice, isOpenSyncModal } = state;
  const flattenedIngredients = state.data?.groups
    ? state.data.groups.flatMap((item) => item.ingredients)
    : [];
  const {
    total_cost_ExGst,
    overhead_cost,
    products_count,
    retailPriceExcGst,
    retailPriceInGst,
    cost_percentage,
    profit_percentage,
    overhead_cost_percentage,
    ingredient_cost_percentage,
  } = state.data;

  const remainingArray = [
    {
      name: "Total Ex GST:",
      base_unit_qty: "",
      cost: total_cost_ExGst,
      base_unit_symbol: "",
    },
    {
      name: "Overhead Costs",
      base_unit_qty: "",
      cost: overhead_cost,
      base_unit_symbol: "",
    },
    {
      name: "Recipe Cost ex GST:",
      base_unit_qty: "",
      cost: Number(total_cost_ExGst) + Number(overhead_cost),
      products_count: products_count,
      base_unit_symbol: "",
    },
  ];

  const formatValue = (values) => {
    const value = Number(values);

    if (value % 1 === 0) {
      return value.toString(); // Whole number, return as-is
    }

    const [intPart, decimalPart = ""] = value.toString().split(".");
    const truncatedDecimals = decimalPart.slice(0, 3).padEnd(3, "0");

    return `${intPart}.${truncatedDecimals}`;
  };

  const columns = [
    {
      header: "Ingredient Name",
      cell: (row, index) => {
        return (
          <>
            {row.name === "Total Ex GST:" ||
            row.name === "Recipe Cost ex GST:" ? (
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {row.name}
              </Typography>
            ) : (
              row.name
            )}
          </>
        );
      },
    },
    {
      header: "Quantity",
      cell: (row, index) => {
        return (
          row.base_unit_qty &&
          formatWeightWithUnits(
            row.base_unit_qty,
            row.base_unit_symbol,
            globalData?.all_Units,
          )
        );
      },
    },
    {
      header: "Cost (Ex GST)",
      cell: (row, index) => {
        const formatCost = (cost) => {
          const value = Number(cost);

          if (value % 1 === 0) {
            return value.toString(); // Whole number, return as-is
          }

          const [intPart, decimalPart = ""] = value.toString().split(".");
          const truncatedDecimals = decimalPart.slice(0, 3).padEnd(3, "0");

          return `${intPart}.${truncatedDecimals}`;
        };
        return (
          <>
            {row.name === "Overhead Costs" ? (
              <InputControl
                name="overhead_cost"
                onChange={onChange}
                // value={
                //   row.cost === "0.000"
                //     ? formatWeightWithUnits(row.cost, "", "")
                //     : row.cost
                // }
                value={isEditoverHead ? row.cost : formatCost(row.cost)}
                onKeyPress={(e) => {
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                size="small"
                disabled={!isEditoverHead}
                InputProps={{
                  startAdornment: <InputAdornment>$</InputAdornment>,
                }}
              />
            ) : row.name === "Recipe Cost ex GST:" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                  ${formatCost(row.cost)}
                </Typography>
                {basicDetailsQuery?.data?.type === "finishedproduct" ? (
                  <>
                    for{" "}
                    <InputControl
                      sx={{ width: "80px" }}
                      name="products_count"
                      onChange={onChange}
                      size="small"
                      disabled={!isEditoverHead}
                      value={row.products_count}
                      error={costvalidator.current.message(
                        "Product Count",
                        row.products_count,
                        "required",
                      )}
                      helperText={costvalidator.current.message(
                        "Product Count",
                        row.products_count,
                        "required",
                      )}
                    />
                    product(s)
                  </>
                ) : null}
              </Box>
            ) : (
              `$${formatCost(row.cost)}`
            )}
          </>
        );
      },
    },
  ];
  const generateStyle = (item) => {
    let style = {};
    if (item.name === "Total Ex GST:" || item.name === "Recipe Cost ex GST:") {
      style = { ...style, backgroundColor: "#F5F8FC" };
    }
    return style;
  };
  return (
    <Stack gap="20px">
      <Stack>
        <Stack
          py="12px"
          backgroundColor="#fff"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          borderBottom={1}
          borderColor="divider"
        >
          <Typography
            variant="h6"
            fontWeight={600}
            lineHeight="19px"
            minWidth="160px"
          >
            Cost Breakdown
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              variant="caption"
              color="#485364"
              fontWeight={600}
              fontSize="12px"
              sx={{ whiteSpace: "nowrap" }}
            >
              Last Updated on: {basicDetailsQuery?.data?.updated_at_user_tz}
            </Typography>
            <Box mt={1}>
              <Divider orientation="vertical" flexItem sx={{ height: 20 }} />
            </Box>

            {isEditable && (
              <Button
                type="button"
                size="small"
                variant="contained"
                color={isEditoverHead ? "primary" : "inherit"}
                onClick={
                  isEditoverHead
                    ? onUpdateOverHead
                    : () => onEditClick("isEditoverHead")
                }
                disabled={updateOverHead?.isPending}
              >
                {updateOverHead?.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isEditoverHead ? (
                  "save"
                ) : (
                  "Edit"
                )}
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" gap="16px" p="12px" backgroundColor="#fff">
          <BasicMuiTableStyled
            data={[...flattenedIngredients, ...remainingArray]}
            columns={columns}
            styleFunc={generateStyle}
            tableStyle={{
              mx: "8px",
              mb: "8px",
              width: "calc(100% - 16px)",
            }}
            disableDefaultStyle
          />
        </Stack>
        {basicDetailsQuery?.data?.type === "finishedproduct" ? (
          <Typography
            variant="caption"
            lineHeight="14px"
            color="#485364"
            sx={{
              width: "50%",
              marginLeft: "auto",
              mt: "-4px",
            }}
          >
            {(() => {
              const totalCost =
                Number(total_cost_ExGst) + Number(overhead_cost);
              const count = Number(products_count) || 1;
              const perProduct = totalCost / count;
              return `$${perProduct.toFixed(3)} per product. This value is used as the cost during POS sync.`;
            })()}
          </Typography>
        ) : null}
      </Stack>

      {basicDetailsQuery?.data?.type === "finishedproduct" && (
        <Box
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            height="54px"
            borderBottom="1px solid"
            borderColor="divider"
          >
            <Typography
              variant="h6"
              fontWeight={600}
              lineHeight="19px"
              minWidth="160px"
            >
              Retail Price
            </Typography>
            {isEditable && (
              <Button
                type="button"
                size="small"
                variant="contained"
                color={isEditPrice ? "primary" : "inherit"}
                onClick={
                  isEditPrice
                    ? onUpdateRetailPriceClick
                    : () => onEditClick("isEditPrice")
                }
              >
                {isEditPrice ? "save" : "Edit"}
              </Button>
            )}
          </Stack>
          <Stack direction="row" gap="16px">
            <InputControl
              label="Retail Price Ex GST"
              InputProps={{
                startAdornment: <InputAdornment>$</InputAdornment>,
              }}
              value={
                isEditPrice ? retailPriceExcGst : formatValue(retailPriceExcGst)
              }
              disabled={!isEditPrice}
              fullWidth
              name="retailPriceExcGst"
              type="text"
              required
              error={validator.current.message(
                "Retail Price Ex GST",
                retailPriceExcGst,
                "required",
              )}
              helperText={validator.current.message(
                "Retail Price Ex GST",
                retailPriceExcGst,
                "required",
              )}
              onChange={onRetailPriceChange}
            />
            <InputControl
              label="Retail Price In GST"
              InputProps={{
                startAdornment: <InputAdornment>$</InputAdornment>,
              }}
              value={
                isEditPrice ? retailPriceInGst : formatValue(retailPriceInGst)
              }
              disabled={!isEditPrice}
              fullWidth
              name="retailPriceInGst"
              type="text"
              required
              error={validator.current.message(
                "Retail Price In GST",
                retailPriceInGst,
                "required",
              )}
              helperText={validator.current.message(
                "Retail Price In GST",
                retailPriceInGst,
                "required",
              )}
              onChange={onRetailPriceChange}
            />
          </Stack>
          <Box
            sx={{
              backgroundColor: "#F5F8FC",
              p: "8px",
            }}
          >
            <Grid
              container
              sx={{
                borderBottom: "1px solid #DFE8F2",
                padding: "10px",
                backgroundColor: "#ffffff",
              }}
            >
              <Stack
                flex={1}
                direction="row"
                justifyContent="space-between"
                height="40px"
              >
                <Typography fontSize="14px" fontWeight={500}>
                  Cost percentage
                </Typography>
                <Typography fontWeight={500}>
                  {formatValue(cost_percentage)}%
                </Typography>
              </Stack>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap="6px"
                  sx={{ width: "100%" }}
                >
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      padding: "8px",
                      backgroundColor: "#F5F8FC",
                      borderRadius: "4px",
                      height: "36px",
                    }}
                  >
                    <Stack
                      flex={1}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography fontWeight={400} fontSize="12px">
                        Ingredient Cost Percentage
                      </Typography>
                      <Typography fontWeight={400} fontSize="12px">
                        {formatValue(ingredient_cost_percentage)}%
                      </Typography>
                    </Stack>
                  </Grid>
                  <Typography
                    fontWeight={500}
                    fontSize="16px"
                    alignContent="center"
                  >
                    +
                  </Typography>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      padding: "8px",
                      backgroundColor: "#F5F8FC",
                      borderRadius: "4px",
                      height: "36px",
                    }}
                  >
                    <Stack
                      flex={1}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography fontWeight={400} fontSize="12px">
                        Overhead Cost Percentage
                      </Typography>
                      <Typography fontWeight={400} fontSize="12px">
                        {formatValue(overhead_cost_percentage)}%
                      </Typography>
                    </Stack>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              sx={{
                padding: "10px",
                backgroundColor: "#ffffff",
                height: "40px",
              }}
            >
              <Stack flex={1} direction="row" justifyContent="space-between">
                <Typography fontSize="14px" fontWeight={500}>
                  Profit percentage
                </Typography>
                <Typography fontWeight={500}>
                  {formatValue(profit_percentage)}%
                </Typography>
              </Stack>
            </Grid>
          </Box>
        </Box>
      )}
      <PosScheduleSyncModal
        open={isOpenSyncModal}
        onClose={() => handleSyncModal(false)}
        isBtnLoading={updateOverHead?.isPending}
        onSave={onUpdateOverHeadSubmit}
        syncType={state?.data?.sync_type}
        setSyncType={(type) => {
          onChange({
            target: {
              name: "sync_type",
              value: type,
            },
          });
        }}
        scheduledDate={state.data?.scheduled_date}
        setScheduledDate={(date) => {
          onChange({
            target: {
              name: "scheduled_date",
              value: date,
            },
          });
        }}
      />
    </Stack>
  );
};
